import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Camera, Award, Users, MapPin, Zap } from 'lucide-react';

const About: React.FC = () => {
  useEffect(() => {
    document.title = 'Hakkımızda - Yüksekkare';
  }, []);

  const stats = [
    {
      icon: <Camera size={32} className="text-primary-orange" />,
      number: '500+',
      label: 'Proje Tamamlandı',
    },
    {
      icon: <Award size={32} className="text-primary-orange" />,
      number: '5+',
      label: 'Yıl Deneyim',
    },
    {
      icon: <Users size={32} className="text-primary-orange" />,
      number: '200+',
      label: 'Mutlu Müşteri',
    },
    {
      icon: <MapPin size={32} className="text-primary-orange" />,
      number: '50+',
      label: 'Şehir',
    },
  ];

  const droneSpecs = [
    { label: 'Model', value: 'DJI Flip Fly RC 2' },
    { label: 'Kamera', value: '4K/60fps Video' },
    { label: 'Fotoğraf', value: '48MP RAW' },
    { label: 'Uçuş Süresi', value: '34 dakika' },
    { label: 'Maksimum Hız', value: '72 km/s' },
    { label: 'Maksimum Yükseklik', value: '500m' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-navy to-blue-900 text-white px-4 sm:px-6 lg:px-8">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Hakkımızda
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            Ankara'nın gökyüzünden en güzel açılarını yakalayan profesyonel
            drone ekibi
          </motion.p>
        </div>
      </section>

      {/* Founder Story */}
      <section className="section-padding bg-white px-4 sm:px-6 lg:px-8">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-6">
                Kurucu Hikayemiz
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  2019 yılında Ankara'da kurulan Yüksekkare, drone
                  teknolojisinin gücünü kullanarak şehrin en güzel açılarını
                  yakalamak amacıyla yola çıktı.
                </p>
                <p>
                  Kurucumuz, yılların deneyimi ile drone pilotluğu konusunda
                  uzmanlaşmış ve Türkiye'nin önde gelen drone operatörlerinden
                  biri haline gelmiştir.
                </p>
                <p>
                  Bugün, Yüksekkare olarak Ankara ve çevresinde yüzlerce
                  başarılı projeye imza attık ve müşterilerimizin hayallerini
                  gerçeğe dönüştürdük.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-primary-orange to-orange-600 p-8 rounded-2xl text-white">
                <h3 className="text-2xl font-bold mb-4">Misyonumuz</h3>
                <p className="text-lg mb-6">
                  "Teknolojinin gücünü kullanarak, müşterilerimizin en özel
                  anlarını ve projelerini havadan görüntüleyerek, onlara
                  unutulmaz deneyimler yaşatmak."
                </p>
                <div className="flex items-center space-x-2">
                  <Zap size={20} />
                  <span className="font-medium">Profesyonellik & Kalite</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="mb-4 flex justify-center">{stat.icon}</div>
                <div className="text-3xl md:text-4xl font-bold text-primary-navy mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Drone Information */}
      <section className="section-padding bg-white px-4 sm:px-6 lg:px-8">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-4">
              Profesyonel Ekipmanımız
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              En son teknoloji DJI drone'ları ile yüksek kaliteli çekimler
              yapıyoruz
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gray-100 p-8 rounded-2xl">
                <h3 className="text-2xl font-bold text-primary-navy mb-6">
                  DJI Flip Fly RC 2
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {droneSpecs.map((spec, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center py-2 border-b border-gray-200"
                    >
                      <span className="font-medium text-gray-700">
                        {spec.label}:
                      </span>
                      <span className="text-primary-navy font-semibold">
                        {spec.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              <div className="card">
                <h4 className="text-xl font-bold text-primary-navy mb-4">
                  Teknolojik Avantajlar
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-primary-orange rounded-full"></div>
                    <span>4K Ultra HD video çekimi</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-primary-orange rounded-full"></div>
                    <span>Gelişmiş stabilizasyon sistemi</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-primary-orange rounded-full"></div>
                    <span>Uzun uçuş süresi</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-primary-orange rounded-full"></div>
                    <span>Hassas GPS navigasyon</span>
                  </li>
                </ul>
              </div>

              <div className="card">
                <h4 className="text-xl font-bold text-primary-navy mb-4">
                  Güvenlik Önlemleri
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-primary-orange rounded-full"></div>
                    <span>Çarpışma önleme sistemi</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-primary-orange rounded-full"></div>
                    <span>Otomatik dönüş özelliği</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-primary-orange rounded-full"></div>
                    <span>Hava durumu analizi</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-primary-orange rounded-full"></div>
                    <span>Profesyonel sigorta</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-4">
              Ekibimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Deneyimli ve uzman ekibimizle her projede mükemmellik hedefliyoruz
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="card text-center"
            >
              <div className="w-24 h-24 bg-primary-orange/10 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Camera size={32} className="text-primary-orange" />
              </div>
              <h3 className="text-xl font-bold text-primary-navy mb-2">
                Baş Pilot
              </h3>
              <p className="text-gray-600 mb-4">
                5+ yıl deneyimli drone pilotu
              </p>
              <div className="flex justify-center space-x-2">
                <span className="px-3 py-1 bg-primary-orange/10 text-primary-orange rounded-full text-sm">
                  DJI Sertifikalı
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="card text-center"
            >
              <div className="w-24 h-24 bg-primary-orange/10 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Award size={32} className="text-primary-orange" />
              </div>
              <h3 className="text-xl font-bold text-primary-navy mb-2">
                Video Editör
              </h3>
              <p className="text-gray-600 mb-4">
                Profesyonel video düzenleme uzmanı
              </p>
              <div className="flex justify-center space-x-2">
                <span className="px-3 py-1 bg-primary-orange/10 text-primary-orange rounded-full text-sm">
                  Adobe Certified
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="card text-center"
            >
              <div className="w-24 h-24 bg-primary-orange/10 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Users size={32} className="text-primary-orange" />
              </div>
              <h3 className="text-xl font-bold text-primary-navy mb-2">
                Proje Yöneticisi
              </h3>
              <p className="text-gray-600 mb-4">
                Müşteri ilişkileri ve proje koordinasyonu
              </p>
              <div className="flex justify-center space-x-2">
                <span className="px-3 py-1 bg-primary-orange/10 text-primary-orange rounded-full text-sm">
                  PMP Sertifikalı
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
